import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormulaBuilder } from '@/components/FormulaBuilder';
import { FormulaScopeHint } from '@/components/semantic-models/FormulaScopeHint';
import { Plus, Trash2, Filter, FunctionSquare, Percent, Layers, Clock } from 'lucide-react';
import { BucketEditor } from '@/components/semantic-models/BucketEditor';
import { api } from '@/lib/api';

const TIME_INTEL_OPS = [
  { value: 'same_period_last_year', label: 'Same Period Last Year', icon: '🗓' },
  { value: 'prior_period', label: 'Prior Period', icon: '◀' },
  { value: 'ytd', label: 'Year-to-Date', icon: 'Σ' },
  { value: 'qtd', label: 'Quarter-to-Date', icon: 'Q' },
  { value: 'mtd', label: 'Month-to-Date', icon: 'M' },
  { value: 'yoy_pct', label: 'YoY % Change', icon: '%' },
  { value: 'ROLLING_DAYS', label: 'Rolling N Days', icon: '↻' },
  { value: 'ROLLING_WEEKS', label: 'Rolling N Weeks', icon: '↻' },
  { value: 'ROLLING_MONTHS', label: 'Rolling N Months', icon: '↻' },
  { value: 'ROLLING_QUARTERS', label: 'Rolling N Quarters', icon: '↻' },
  { value: 'ROLLING_YEARS', label: 'Rolling N Years', icon: '↻' },
];

const OPS = [
  { v: 'equals', l: '=' }, { v: 'not_equals', l: '≠' }, { v: 'in', l: 'in (comma list)' },
  { v: 'not_in', l: 'not in (comma list)' },
  { v: 'gt', l: '>' }, { v: 'gte', l: '≥' }, { v: 'lt', l: '<' }, { v: 'lte', l: '≤' },
  { v: 'like', l: 'like (pattern)' }, { v: 'between', l: 'between' },
  { v: 'is_null', l: 'is null' }, { v: 'is_not_null', l: 'is not null' },
];
const NULL_OPS = new Set(['is_null', 'is_not_null']);

/**
 * Add Calculated Measure dialog. Supports two modes:
 *  - Formula: a model-level expression (existing behaviour).
 *  - Scoped (set algebra): aggregate a base field only over rows matching the filters,
 *    independent of the query's row context (GAP-3.3).
 */
export function AddMeasureDialog({
  open, onOpenChange,
  modelId,
  measureName, setMeasureName,
  measureFormula, setMeasureFormula,
  measureCursorPos, setMeasureCursorPos,
  measureEntity, setMeasureEntity,
  measureDesc, setMeasureDesc,
  measureFolder, setMeasureFolder,
  entities,
  measures,
  saving,
  onCreate,
  onAiGenerate,
  onResetForm,
  scopeMode, setScopeMode,
  scopeBaseField, setScopeBaseField,
  scopeAgg, setScopeAgg,
  scopeFilters, setScopeFilters,
  scopeIgnore, setScopeIgnore,
  scopePct, setScopePct,
  tiMode, setTiMode, tiOp, setTiOp, tiOf, setTiOf, tiN, setTiN,
}) {
  const allFields = entities.flatMap(ent => (ent.attributes || []).map(a => ({ ...a, entityName: ent.name })));
  const numericFields = allFields.filter(f => f.semantic_type === 'measure' || ['integer', 'float', 'number'].includes(f.data_type));
  // Source measures a time-intel measure can wrap: model calc measures + numeric fields.
  const tiSourceOptions = Array.from(new Set([
    ...(measures || []).map(m => m.name),
    ...numericFields.map(f => f.name),
  ].filter(Boolean)));
  const dateFields = allFields.filter(f => (f.data_type || '').toLowerCase().includes('date'));
  const textFields = allFields.filter(f => ['string', 'text', 'varchar', 'character'].includes((f.data_type || '').toLowerCase()));
  const selectedEntityForBuckets = entities.find(e => e.entity_id === measureEntity) || entities[0];
  // Third mode: bucketing. Emitted formula flows through the standard
  // formula create path — no parent-side plumbing needed.
  const [bucketMode, setBucketMode] = React.useState(false);
  const [bucketValid, setBucketValid] = React.useState(false);
  const addFilter = () => setScopeFilters([...(scopeFilters || []), { field: '', operator: 'equals', value: '' }]);
  const updFilter = (i, patch) => setScopeFilters((scopeFilters || []).map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const rmFilter = (i) => setScopeFilters((scopeFilters || []).filter((_, idx) => idx !== i));
  // % of grand total only needs a base field; otherwise a scoped measure needs ≥1 filter.
  const scopeValid = scopeBaseField && (scopePct || ((scopeFilters || []).length > 0 && (scopeFilters || []).every(f => f.field && (NULL_OPS.has(f.operator) || (f.operator === 'between' ? (Array.isArray(f.value) && f.value[0] !== '' && f.value[1] !== '') : f.value !== '')))));
  const canCreate = measureName.trim() && (
    tiMode ? (!!tiOf && (!/^ROLLING_/.test(tiOp) || (parseInt(tiN, 10) > 0)))
    : bucketMode ? bucketValid && measureFormula.trim()
    : scopeMode ? scopeValid
    : measureFormula.trim()
  );

  const setModeFormula = () => { setBucketMode(false); setScopeMode(false); setTiMode && setTiMode(false); };
  const setModeScoped  = () => { setBucketMode(false); setScopeMode(true); setTiMode && setTiMode(false); };
  const setModeBuckets = () => { setBucketMode(true);  setScopeMode(false); setTiMode && setTiMode(false); setMeasureFormula(''); };
  const setModeTimeIntel = () => { setBucketMode(false); setScopeMode(false); setTiMode && setTiMode(true); setMeasureFormula(''); };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v && onResetForm) onResetForm(); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader><DialogTitle className="font-heading">Add Calculated Measure</DialogTitle></DialogHeader>

        {/* Mode toggle */}
        <div className="flex items-center gap-1 p-0.5 bg-muted/40 rounded-lg w-fit -mt-1">
          <button onClick={setModeFormula} data-testid="measure-mode-formula"
            className={`px-3 py-1 rounded-md text-[11px] flex items-center gap-1.5 transition-all ${!scopeMode && !bucketMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            <FunctionSquare className="w-3 h-3" /> Formula
          </button>
          <button onClick={setModeScoped} data-testid="measure-mode-scoped"
            className={`px-3 py-1 rounded-md text-[11px] flex items-center gap-1.5 transition-all ${scopeMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            <Filter className="w-3 h-3" /> Scoped (set filter)
          </button>
          <button onClick={setModeBuckets} data-testid="measure-mode-buckets"
            className={`px-3 py-1 rounded-md text-[11px] flex items-center gap-1.5 transition-all ${bucketMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            <Layers className="w-3 h-3" /> Bins & Groups
          </button>
          <button onClick={setModeTimeIntel} data-testid="measure-mode-time-intel"
            className={`px-3 py-1 rounded-md text-[11px] flex items-center gap-1.5 transition-all ${tiMode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            <Clock className="w-3 h-3" /> Time Intelligence
          </button>
        </div>

        <div className="space-y-3 mt-1 flex-1 overflow-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Measure Name</Label>
              <Input value={measureName} onChange={e => setMeasureName(e.target.value)} placeholder={scopeMode ? 'e.g. Revenue (West)' : 'e.g. Loss Ratio'} className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm text-foreground" data-testid="measure-name-input" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Source Entity</Label>
              <Select value={measureEntity || '_none_'} onValueChange={v => setMeasureEntity(v === '_none_' ? '' : v)}>
                <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm text-foreground"><SelectValue placeholder="Select entity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">None</SelectItem>
                  {[...entities].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(e => <SelectItem key={e.entity_id} value={e.entity_id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] text-muted-foreground">Description</Label>
              <Input value={measureDesc} onChange={e => setMeasureDesc(e.target.value)} placeholder="What this measure calculates" className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm text-foreground" />
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Display folder</Label>
              <Input value={measureFolder || ''} onChange={e => setMeasureFolder(e.target.value)} placeholder="e.g. Profitability" className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm text-foreground" data-testid="measure-folder-input" />
            </div>
          </div>

          {!scopeMode && !bucketMode && !tiMode && (
            <>
              <FormulaBuilder formula={measureFormula} onChange={setMeasureFormula} cursorPos={measureCursorPos} onCursorChange={setMeasureCursorPos} fields={allFields} measures={(measures || []).filter(m => !measureName || m.name !== measureName)} onAiGenerate={onAiGenerate} nameValue={measureName}
                onTest={async (formula, name) => {
                  if (!modelId) return { ok: false, error: 'No active model' };
                  const comp = await api.testMeasureFormula(modelId, { name: name || 'Test Measure', formula });
                  if (!comp.ok) return comp;
                  const dim = textFields[0] || dateFields[0] || allFields.find(f => f.semantic_type === 'dimension');
                  const dims = dim ? [dim.name] : [];
                  const col = name || 'Test Measure';
                  try {
                    const res = await api.executeQuery({ model_id: modelId, dimensions: dims, measures: [col], calculations: [comp.spec], limit: 3 });
                    const rows = (res.data || []).slice(0, 3);
                    return { ok: true, rows, columns: dims.length ? [dims[0], col] : [col] };
                  } catch (e) {
                    return { ok: false, error: 'Compiled OK but failed to run: ' + (e?.message || e) };
                  }
                }}
              />
              <FormulaScopeHint modelId={modelId} formula={measureFormula} />
            </>
          )}

          {tiMode && (
            <div className="space-y-3" data-testid="time-intel-fields">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] text-muted-foreground">Operator</Label>
                  <Select value={tiOp} onValueChange={setTiOp}>
                    <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm" data-testid="ti-op-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIME_INTEL_OPS.map(o => <SelectItem key={o.value} value={o.value}><span className="inline-block w-4 text-center mr-1">{o.icon}</span>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {/^ROLLING_/.test(tiOp) && (
                    <div className="mt-1.5">
                      <Label className="text-[10px] text-muted-foreground">Window size (N {tiOp.replace('ROLLING_', '').toLowerCase()})</Label>
                      <Input type="number" min={1} value={tiN ?? 3} onChange={e => setTiN && setTiN(e.target.value)} className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm h-8" data-testid="ti-n-input" />
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Source Measure (of)</Label>
                  <Select value={tiOf || '_pick_'} onValueChange={v => setTiOf(v === '_pick_' ? '' : v)}>
                    <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm" data-testid="ti-of-select"><SelectValue placeholder="Pick a measure…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_pick_" disabled>Pick a measure…</SelectItem>
                      {tiSourceOptions.map(nm => <SelectItem key={nm} value={nm}>{nm}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-emerald-500/[0.04] border border-emerald-500/20" data-testid="ti-preview">
                <p className="text-[9px] uppercase tracking-wider text-emerald-400/80 font-semibold mb-1">Preview</p>
                <p className="text-xs font-mono text-emerald-300">{measureName || 'unnamed'} = <span className="text-cyan-400">{tiOp}</span>(<span className="text-amber-300">{tiOf || '?'}</span>{/^ROLLING_/.test(tiOp) ? <span className="text-fuchsia-300">, {tiN ?? 3}</span> : null})</p>
                <p className="text-[10px] text-muted-foreground mt-1 leading-snug">Applied at query time — needs a time dimension in the result. The source measure must also be available in the query.</p>
              </div>
            </div>
          )}

          {bucketMode && (
            <div data-testid="measure-buckets-builder">
              <BucketEditor
                modelId={modelId}
                entityName={selectedEntityForBuckets?.name}
                fields={allFields}
                fieldsNumeric={numericFields}
                fieldsDate={dateFields}
                fieldsText={textFields}
                testIdPrefix="measure-bucket"
                onChange={({ valid, formula: fx }) => {
                  setBucketValid(valid);
                  setMeasureFormula(valid ? (fx || '') : '');
                }}
              />
            </div>
          )}

          {scopeMode && (
            <div className="space-y-3" data-testid="scoped-measure-builder">
              <label className="flex items-center gap-2 text-[11px] text-foreground cursor-pointer rounded-lg bg-primary/5 border border-primary/20 px-3 py-2" data-testid="pct-of-total-toggle">
                <input type="checkbox" checked={!!scopePct} onChange={e => setScopePct(e.target.checked)} className="w-3.5 h-3.5 rounded accent-primary" data-testid="pct-of-total-checkbox" />
                <Percent className="w-3.5 h-3.5 text-primary" />
                <span><span className="font-medium">Show as % of grand total</span> — one-click share-of-total (filters optional)</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] text-muted-foreground">Aggregation</Label>
                  <Select value={scopeAgg || 'sum'} onValueChange={setScopeAgg}>
                    <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm" data-testid="scope-agg"><SelectValue /></SelectTrigger>
                    <SelectContent>{['sum', 'avg', 'min', 'max', 'count'].map(a => <SelectItem key={a} value={a}>{a.toUpperCase()}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Of field</Label>
                  <Select value={scopeBaseField || ''} onValueChange={setScopeBaseField}>
                    <SelectTrigger className="rounded-lg bg-muted/50 border-border/50 mt-0.5 text-sm" data-testid="scope-base-field"><SelectValue placeholder="numeric field" /></SelectTrigger>
                    <SelectContent>{numericFields.map(f => <SelectItem key={f.name} value={f.name}>{f.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-[10px] text-muted-foreground">Only over rows where…</Label>
                  <Button size="sm" variant="ghost" onClick={addFilter} className="h-6 text-[10px] gap-1" data-testid="scope-add-filter"><Plus className="w-3 h-3" /> Filter</Button>
                </div>
                <div className="space-y-1.5">
                  {(scopeFilters || []).length === 0 && <p className="text-[10px] text-muted-foreground/80 italic">Add at least one filter to scope this measure.</p>}
                  {(scopeFilters || []).map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5" data-testid={`scope-filter-${i}`}>
                      <Select value={f.field || ''} onValueChange={v => updFilter(i, { field: v })}>
                        <SelectTrigger className="h-7 text-[11px] rounded bg-muted/40 border-border/40 flex-1"><SelectValue placeholder="field" /></SelectTrigger>
                        <SelectContent>{allFields.map(af => <SelectItem key={af.name} value={af.name} className="text-xs">{af.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={f.operator || 'equals'} onValueChange={v => updFilter(i, { operator: v, value: v === 'between' ? ['', ''] : (NULL_OPS.has(v) ? '' : (Array.isArray(f.value) ? '' : f.value)) })}>
                        <SelectTrigger className="h-7 text-[11px] rounded bg-muted/40 border-border/40 w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>{OPS.map(o => <SelectItem key={o.v} value={o.v} className="text-xs">{o.l}</SelectItem>)}</SelectContent>
                      </Select>
                      {f.operator === 'between' ? (
                        <div className="flex items-center gap-1 flex-1">
                          <Input value={Array.isArray(f.value) ? (f.value[0] ?? '') : ''} onChange={e => updFilter(i, { value: [e.target.value, Array.isArray(f.value) ? (f.value[1] ?? '') : ''] })} placeholder="from" className="h-7 text-[11px] rounded bg-muted/40 border-border/40 flex-1" data-testid={`scope-filter-value-${i}`} />
                          <span className="text-[10px] text-muted-foreground">and</span>
                          <Input value={Array.isArray(f.value) ? (f.value[1] ?? '') : ''} onChange={e => updFilter(i, { value: [Array.isArray(f.value) ? (f.value[0] ?? '') : '', e.target.value] })} placeholder="to" className="h-7 text-[11px] rounded bg-muted/40 border-border/40 flex-1" data-testid={`scope-filter-value2-${i}`} />
                        </div>
                      ) : (
                        <Input value={NULL_OPS.has(f.operator) ? '' : (Array.isArray(f.value) ? '' : f.value)} onChange={e => updFilter(i, { value: e.target.value })} disabled={NULL_OPS.has(f.operator)} placeholder={NULL_OPS.has(f.operator) ? '—' : (f.operator === 'like' ? '%pattern%' : 'value')} className="h-7 text-[11px] rounded bg-muted/40 border-border/40 flex-1 disabled:opacity-40" data-testid={`scope-filter-value-${i}`} />
                      )}
                      <button onClick={() => rmFilter(i)} className="p-1 text-muted-foreground/70 hover:text-rose-400"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground/80 mt-1">e.g. <span className="font-mono">SUM(charge_amount)</span> only where <span className="font-mono">primary_condition = Muscle Spasms</span> — computed independently of the chart's dimensions.</p>
              </div>
              <div className="pt-2 border-t border-border/20" data-testid="ignore-filters-block">
                <Label className="text-[10px] text-muted-foreground">Ignore filters on (context switch)</Label>
                <Input value={scopeIgnore || ''} onChange={e => setScopeIgnore(e.target.value)} placeholder="e.g. region, line_of_business  ·  or  *  for all" className="rounded-lg bg-muted/40 border-border/40 mt-0.5 text-xs font-mono h-7" data-testid="scope-ignore-input" />
                <p className="text-[10px] text-muted-foreground/80 mt-1">DAX <span className="font-mono">ALL()</span>-style: this measure breaks out of the listed dashboard/query filters (use <span className="font-mono">*</span> to ignore every filter — great for "% of grand total").</p>
              </div>
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-border/30 mt-2">
          <Button onClick={onCreate} disabled={saving || !canCreate} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="confirm-add-measure">
            <Plus className="w-4 h-4 mr-2" />Create Measure
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
